import React, { useState } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import { FaEnvelope, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { login } from "../../services/authServices";

const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (serverError) {
      setServerError("");
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    setServerError("");

    try {
      await login(formData.email, formData.password);
      navigate("/admin-dashboard");
    } catch (error) {
      setServerError(
        error?.response?.data?.message ||
          error?.message ||
          "Login failed. Please check your credentials.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Login | Trans World Shipping</title>
        <meta
          name="description"
          content="Login to your Trans World Shipping account to track shipments, request rates, and manage your shipping needs."
        />
        <link
          rel="canonical"
          href="https://transworldshippingcompany.com/login"
        />
      </Helmet>

      <TheLogin>
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <h1>Welcome Back</h1>
              <p>Sign in to your account to continue</p>
            </div>

            {serverError && <div className="server-error">{serverError}</div>}

            <form onSubmit={handleSubmit} noValidate>
              <div className="form-group">
                <label htmlFor="email">
                  Email Address <span className="required">*</span>
                </label>
                <div className="input-wrapper">
                  <FaEnvelope className="input-icon" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className={errors.email ? "error" : ""}
                    disabled={isLoading}
                    autoComplete="email"
                  />
                </div>
                {errors.email && (
                  <span className="error-message">{errors.email}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="password">
                  Password <span className="required">*</span>
                </label>
                <div className="input-wrapper">
                  <FaLock className="input-icon" />
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    className={errors.password ? "error" : ""}
                    disabled={isLoading}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword((prev) => !prev)}
                    disabled={isLoading}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {errors.password && (
                  <span className="error-message">{errors.password}</span>
                )}
              </div>

              <div className="form-options">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                  <span>Remember me</span>
                </label>
                <Link to="/forgot-password" className="forgot-link">
                  Forgot Password?
                </Link>
              </div>

              <button type="submit" className="login-btn" disabled={isLoading}>
                {isLoading ? "Signing in…" : "Sign In"}
              </button>
            </form>

            <div className="register-link">
              Don't have an account?{" "}
              <Link to="/register">Create an account</Link>
            </div>
          </div>
        </div>
      </TheLogin>
    </>
  );
};

const TheLogin = styled.div`
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(135deg, #0b1e33 0%, #0a1a2c 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;

  .login-container {
    width: 100%;
    max-width: 450px;
    margin: 0 auto;
  }

  .login-card {
    background: white;
    border-radius: 16px;
    padding: 2.5rem;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-5px);
    }
  }

  .login-header {
    text-align: center;
    margin-bottom: 2rem;

    h1 {
      font-size: 2rem;
      color: #0b1e33;
      font-weight: 700;
      margin-bottom: 0.5rem;
    }

    p {
      font-size: 0.95rem;
      color: #718096;
    }
  }

  .server-error {
    background: #fee2e2;
    border: 1px solid #fecaca;
    color: #dc2626;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    font-size: 0.85rem;
    margin-bottom: 1.5rem;
    text-align: center;
  }

  .form-group {
    margin-bottom: 1.5rem;

    label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: 600;
      color: #0b1e33;
      font-size: 0.9rem;

      .required {
        color: #e53e3e;
        margin-left: 2px;
      }
    }

    .input-wrapper {
      position: relative;

      .input-icon {
        position: absolute;
        left: 1rem;
        top: 50%;
        transform: translateY(-50%);
        color: #a0aec0;
        font-size: 1rem;
        pointer-events: none;
      }

      input {
        width: 100%;
        padding: 0.85rem 1rem 0.85rem 2.75rem;
        border: 1.5px solid #e2e8f0;
        border-radius: 10px;
        font-size: 1rem;
        transition: all 0.3s ease;
        font-family: inherit;
        color: #0b1e33;

        &:focus {
          outline: none;
          border-color: #d4af37;
          box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
        }

        &.error {
          border-color: #e53e3e;

          &:focus {
            box-shadow: 0 0 0 3px rgba(229, 62, 62, 0.1);
          }
        }

        &:disabled {
          background: #f7fafc;
          cursor: not-allowed;
        }
      }

      .password-toggle {
        position: absolute;
        right: 1rem;
        top: 50%;
        transform: translateY(-50%);
        background: none;
        border: none;
        cursor: pointer;
        color: #a0aec0;
        font-size: 1rem;
        display: flex;
        align-items: center;
        padding: 0;
        transition: color 0.2s;

        &:hover {
          color: #d4af37;
        }

        &:disabled {
          cursor: not-allowed;
        }
      }
    }

    .error-message {
      display: block;
      margin-top: 0.5rem;
      font-size: 0.8rem;
      color: #e53e3e;
    }
  }

  .form-options {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;

    .checkbox-label {
      display: flex;
      align-items: center;
      cursor: pointer;
      font-size: 0.9rem;
      color: #4a5568;
      gap: 0.5rem;

      input {
        cursor: pointer;
        width: 16px;
        height: 16px;
        accent-color: #d4af37;

        &:disabled {
          cursor: not-allowed;
        }
      }

      span {
        user-select: none;
      }
    }

    .forgot-link {
      font-size: 0.9rem;
      color: #d4af37;
      text-decoration: none;
      transition: color 0.2s;

      &:hover {
        color: #c4a12e;
        text-decoration: underline;
      }
    }
  }

  .login-btn {
    width: 100%;
    background: #d4af37;
    color: #0b1e33;
    border: none;
    padding: 0.85rem;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: inherit;
    margin-bottom: 1.5rem;

    &:hover:not(:disabled) {
      background: #c4a12e;
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(212, 175, 55, 0.3);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }

  .register-link {
    text-align: center;
    font-size: 0.9rem;
    color: #718096;

    a {
      color: #d4af37;
      text-decoration: none;
      font-weight: 600;
      transition: color 0.2s;

      &:hover {
        color: #c4a12e;
        text-decoration: underline;
      }
    }
  }

  @media screen and (max-width: 600px) {
    padding: 1rem;

    .login-card {
      padding: 1.5rem;
    }

    .login-header h1 {
      font-size: 1.5rem;
    }

    .form-options {
      flex-direction: column;
      gap: 0.75rem;
      align-items: flex-start;
    }
  }

  @media screen and (max-width: 480px) {
    .login-card {
      padding: 1.25rem;
    }

    .login-header h1 {
      font-size: 1.3rem;
    }

    .login-header p {
      font-size: 0.85rem;
    }
  }
`;

export default Login;
